import React, { useState, useEffect } from 'react';
import { Building, MapPin, Calendar, User, Phone, Mail } from 'lucide-react';
import { LocationSelector } from '../../ui/LocationSelector';

interface Step1Props {
  data: any;
  onUpdate: (data: any) => void;
  onNext: () => void;
  onCancel: () => void;
  promotionId?: string;
}

export const Step1InfosPromotion: React.FC<Step1Props> = ({
  data,
  onUpdate,
  onNext,
  onCancel,
  promotionId,
}) => {
  const [formData, setFormData] = useState(data);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (data && Object.keys(data).length > 0) {
      setFormData(data);
    }
  }, [data]);

  const handleChange = (field: string, value: any) => {
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);
    setErrors({ ...errors, [field]: '' });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    handleChange(e.target.name, e.target.value);
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name) newErrors.name = 'Le nom est obligatoire';
    if (!formData.address) newErrors.address = "L'adresse est obligatoire";
    if (!formData.city) newErrors.city = 'La ville est obligatoire';
    if (!formData.wilaya) newErrors.wilaya = 'La wilaya est obligatoire';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validate()) {
      onUpdate(formData);

      if (promotionId) {
        try {
          const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';

          const response = await fetch(`${apiUrl}/promotions/${promotionId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(formData)
          });

          if (!response.ok) {
            throw new Error(`Erreur ${response.status}: ${response.statusText}`);
          }

          await response.json();
          alert('Modification enregistrée avec succès!');
          window.location.href = `/promotions/${promotionId}`;
        } catch (error) {
          console.error('Erreur lors de la sauvegarde:', error);
          alert('Erreur lors de la sauvegarde. Vérifiez la console pour plus de détails.');
        }
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Informations générales
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Commençons par les informations de base de votre promotion
        </p>
      </div>

      {/* Nom de la promotion */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Nom de la promotion *
        </label>
        <div className="relative">
          <Building className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white ${
              errors.name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
            }`}
          />
        </div>
        {errors.name && (
          <p className="mt-1 text-sm text-red-600">{errors.name}</p>
        )}
      </div>

      {/* Adresse */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Adresse complète *
        </label>
        <div className="relative">
          <MapPin className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
          <input
            type="text"
            name="address"
            value={formData.address}
            onChange={handleInputChange}
            className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white ${
              errors.address ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
            }`}
          />
        </div>
        {errors.address && (
          <p className="mt-1 text-sm text-red-600">{errors.address}</p>
        )}
      </div>

      {/* Wilaya et Ville avec LocationSelector */}
      <div>
        <LocationSelector
          wilayaName="wilaya"
          wilayaValue={formData.wilaya}
          communeName="city"
          communeValue={formData.city}
          onWilayaChange={handleInputChange}
          onCommuneChange={handleInputChange}
        />
        {errors.wilaya && (
          <p className="mt-1 text-sm text-red-600">{errors.wilaya}</p>
        )}
        {errors.city && (
          <p className="mt-1 text-sm text-red-600">{errors.city}</p>
        )}
      </div>

      {/* Promoteur - 3 colonnes */}
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Nom du promoteur
          </label>
          <div className="relative">
            <User className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
            <input
              type="text"
              name="promoter_name"
              value={formData.promoter_name}
              onChange={handleInputChange}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Téléphone promoteur
          </label>
          <div className="relative">
            <Phone className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
            <input
              type="tel"
              name="promoter_phone"
              value={formData.promoter_phone}
              onChange={handleInputChange}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Email promoteur
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
            <input
              type="email"
              name="promoter_email"
              value={formData.promoter_email}
              onChange={handleInputChange}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>
      </div>

      {/* Date de livraison */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Date de livraison prévue
        </label>
        <div className="relative">
          <Calendar className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
          <input
            type="date"
            name="delivery_date"
            value={formData.delivery_date}
            onChange={handleInputChange}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
          />
        </div>
      </div>

      {/* Avancement des travaux */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Avancement des travaux : {formData.construction_progress || 0}%
        </label>
        <input
          type="range"
          min="0"
          max="100"
          name="construction_progress"
          value={formData.construction_progress || 0}
          onChange={(e) => handleChange('construction_progress', parseInt(e.target.value))}
          className="w-full accent-green-600"
        />
        <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mt-1">
          <span>0%</span>
          <span>25%</span>
          <span>50%</span>
          <span>75%</span>
          <span>100%</span>
        </div>
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Description
        </label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          rows={4}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
        />
      </div>

      {/* Équipements et Services - 3 colonnes */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Équipements et Services
        </h3>
        <div className="grid grid-cols-3 gap-x-8 gap-y-4">
          {/* Colonne 1 - Équipements collectifs */}
          <div className="space-y-3">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                name="has_collective_pool"
                checked={formData.has_collective_pool || false}
                onChange={(e) => handleChange('has_collective_pool', e.target.checked)}
                className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500 dark:bg-gray-700 dark:border-gray-600"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Piscine collective</span>
            </label>

            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                name="has_private_pool"
                checked={formData.has_private_pool || false}
                onChange={(e) => handleChange('has_private_pool', e.target.checked)}
                className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500 dark:bg-gray-700 dark:border-gray-600"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Piscine privée</span>
            </label>

            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                name="has_water_tank"
                checked={formData.has_water_tank || false}
                onChange={(e) => handleChange('has_water_tank', e.target.checked)}
                className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500 dark:bg-gray-700 dark:border-gray-600"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Bâche à eau</span>
            </label>
          </div>

          {/* Colonne 2 - Sécurité et confort */}
          <div className="space-y-3">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                name="has_security_cameras"
                checked={formData.has_security_cameras || false}
                onChange={(e) => handleChange('has_security_cameras', e.target.checked)}
                className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500 dark:bg-gray-700 dark:border-gray-600"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Caméras de surveillance</span>
            </label>

            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                name="has_digicode"
                checked={formData.has_digicode || false}
                onChange={(e) => handleChange('has_digicode', e.target.checked)}
                className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500 dark:bg-gray-700 dark:border-gray-600"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Digicode</span>
            </label>

            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                name="has_guardian"
                checked={formData.has_guardian || false}
                onChange={(e) => handleChange('has_guardian', e.target.checked)}
                className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500 dark:bg-gray-700 dark:border-gray-600"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Gardien</span>
            </label>

            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                name="has_intercom"
                checked={formData.has_intercom || false}
                onChange={(e) => handleChange('has_intercom', e.target.checked)}
                className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500 dark:bg-gray-700 dark:border-gray-600"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Interphone</span>
            </label>
          </div>

          {/* Colonne 3 - Systèmes et stationnement */}
          <div className="space-y-3">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                name="has_central_heating"
                checked={formData.has_central_heating || false}
                onChange={(e) => handleChange('has_central_heating', e.target.checked)}
                className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500 dark:bg-gray-700 dark:border-gray-600"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Chauffage central</span>
            </label>

            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                name="has_central_air_conditioning"
                checked={formData.has_central_air_conditioning || false}
                onChange={(e) => handleChange('has_central_air_conditioning', e.target.checked)}
                className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500 dark:bg-gray-700 dark:border-gray-600"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Climatisation centrale</span>
            </label>

            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                name="has_individual_parking_box"
                checked={formData.has_individual_parking_box || false}
                onChange={(e) => handleChange('has_individual_parking_box', e.target.checked)}
                className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500 dark:bg-gray-700 dark:border-gray-600"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Box de stationnement individuel</span>
            </label>
          </div>
        </div>
      </div>

      {/* Boutons */}
      <div className="flex justify-between pt-6 border-t dark:border-gray-700">
        <button
          type="button"
          onClick={onCancel}
          className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          Annuler
        </button>

        <div className="flex gap-3">
          <button
            type="submit"
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            Enregistrer
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              if (validate()) {
                onUpdate(formData);
                onNext();
              }
            }}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Suivant →
          </button>
        </div>
      </div>
    </form>
  );
};
